import React, { useState, useEffect, useCallback } from 'react';
import { 
  ArrowLeft, ArrowRight, Check, X, ChevronRight,
  Phone, Monitor, Box, Smartphone, Camera, Volume2, Battery,
  Wifi, Bluetooth, Mic, Speaker, Zap, Fingerprint, Power,
  MapPin, Calendar, CreditCard, Gift, Building, RefreshCw,
  Package, FileText, Shield, CheckCircle
} from 'lucide-react';
import axios from '../lib/axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Theme
const THEME = {
  primary: '#2b3380',
  primaryLight: '#e8eaf6',
  success: '#10B981',
  warning: '#F59E0B',
  danger: '#EF4444'
};

// Icon mapping
const IconMap = {
  'monitor': Monitor, 'box': Box, 'smartphone': Smartphone, 'camera': Camera,
  'volume-2': Volume2, 'battery': Battery, 'wifi': Wifi, 'bluetooth': Bluetooth,
  'mic': Mic, 'speaker': Speaker, 'zap': Zap, 'fingerprint': Fingerprint,
  'power': Power, 'gift': Gift, 'building': Building, 'package': Package
};

export default function CQCFlow() {
  const [loading, setLoading] = useState(true);
  const [sessionId, setSessionId] = useState(null);
  const [currentScreen, setCurrentScreen] = useState(null);
  const [screenConfig, setScreenConfig] = useState(null);
  const [answers, setAnswers] = useState({});
  const [completedScreens, setCompletedScreens] = useState([]);
  const [priceData, setPriceData] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  // Selection states
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [selectedModel, setSelectedModel] = useState(null);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [currentAnswers, setCurrentAnswers] = useState({});
  
  // Data states
  const [brands, setBrands] = useState([]);
  const [models, setModels] = useState([]);
  const [variants, setVariants] = useState([]);
  const [mbp, setMbp] = useState(0);
  
  // OTP states
  const [mobile, setMobile] = useState('');
  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [testOtp, setTestOtp] = useState('');
  
  // Checkout states
  const [address, setAddress] = useState({ name: '', phone: '', line1: '', city: '', pincode: '' });
  const [pickupDate, setPickupDate] = useState('');
  const [pickupSlot, setPickupSlot] = useState('');
  const [paymentMode, setPaymentMode] = useState('');

  // Initialize session
  useEffect(() => {
    initSession();
  }, []);

  const initSession = async () => {
    try {
      const res = await axios.post(`${API_URL}/api/cqc/session/create`, { device_type: 'mobile' });
      setSessionId(res.data.session_id);
      setCurrentScreen(res.data.current_screen);
      setScreenConfig(res.data.screen_config);
      
      // Load brands
      const brandsRes = await axios.get(`${API_URL}/api/cqc/brands`);
      setBrands(brandsRes.data.data || []);
    } catch (error) {
      console.error('Failed to init session:', error);
      toast.error('Failed to start. Please refresh.');
    } finally {
      setLoading(false);
    }
  };

  // Load models when brand is selected
  const loadModels = async (brandId) => {
    try {
      const res = await axios.get(`${API_URL}/api/cqc/models/${brandId}`);
      setModels(res.data.data || []);
    } catch (error) {
      console.error('Failed to load models:', error);
    }
  };

  // Load variants when model is selected
  const loadVariants = async (modelId) => {
    try {
      const res = await axios.get(`${API_URL}/api/cqc/variants/${modelId}`);
      setVariants(res.data.data || []);
    } catch (error) {
      console.error('Failed to load variants:', error);
    }
  };

  // Submit answer and move to next screen
  const submitAnswer = async () => {
    if (!sessionId || !currentScreen) return;
    setSubmitting(true);
    
    try {
      let answerData = { ...currentAnswers };
      
      // Add specific data based on screen
      if (currentScreen === 'brand_selection' && selectedBrand) {
        answerData = { brand_id: selectedBrand._id || selectedBrand.brand_id };
      } else if (currentScreen === 'model_selection' && selectedModel) {
        answerData = { model_id: selectedModel._id };
      } else if (currentScreen === 'variant_selection' && selectedVariant) {
        answerData = { variant_id: selectedVariant._id || selectedVariant.variant_id };
      }
      
      const res = await axios.post(`${API_URL}/api/cqc/session/${sessionId}/answer`, {
        session_id: sessionId,
        screen_id: currentScreen,
        answers: answerData
      });
      
      // Update state
      setAnswers(prev => ({ ...prev, [currentScreen]: answerData }));
      setCompletedScreens(prev => [...prev, currentScreen]);
      setCurrentScreen(res.data.next_screen);
      setScreenConfig(res.data.screen_config);
      setCurrentAnswers({});
      
      if (res.data.price_data) {
        setPriceData(res.data.price_data);
        setMbp(res.data.price_data.mbp);
      }
      
      // Special handling for mbp_reveal
      if (currentScreen === 'variant_selection') {
        // Fetch session to get MBP
        const sessionRes = await axios.get(`${API_URL}/api/cqc/session/${sessionId}`);
        setMbp(sessionRes.data.data.mbp || 15000);
      }
    } catch (error) {
      console.error('Submit failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit');
    } finally {
      setSubmitting(false);
    }
  };

  // Send OTP
  const sendOtp = async () => {
    if (!mobile || mobile.length !== 10) {
      toast.error('Please enter valid 10-digit mobile number');
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API_URL}/api/cqc/otp/send`, {
        session_id: sessionId,
        mobile: mobile
      });
      setOtpSent(true);
      setTestOtp(res.data.otp_for_testing); // For testing
      toast.success('OTP sent!');
    } catch (error) {
      toast.error('Failed to send OTP');
    } finally {
      setSubmitting(false);
    }
  };

  // Verify OTP
  const verifyOtp = async () => {
    if (!otp || otp.length !== 6) {
      toast.error('Please enter 6-digit OTP');
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API_URL}/api/cqc/otp/verify`, {
        session_id: sessionId,
        mobile: mobile,
        otp: otp
      });
      setPriceData(res.data.price_data);
      setCurrentScreen(res.data.next_screen);
      setScreenConfig(null); // Will be loaded
      toast.success('Price locked!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid OTP');
    } finally {
      setSubmitting(false);
    }
  };

  // Submit address
  const submitAddress = async () => {
    if (!address.name || !address.phone || !address.line1 || !address.pincode) {
      toast.error('Please fill all fields');
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API_URL}/api/cqc/checkout/address`, {
        session_id: sessionId,
        address: address
      });
      setCurrentScreen(res.data.next_screen);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Address not serviceable');
    } finally {
      setSubmitting(false);
    }
  };

  // Submit pickup slot
  const submitSlot = async () => {
    if (!pickupDate || !pickupSlot) {
      toast.error('Please select date and time');
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API_URL}/api/cqc/checkout/slot`, {
        session_id: sessionId,
        date: pickupDate,
        time_slot: pickupSlot
      });
      setCurrentScreen(res.data.next_screen);
    } catch (error) {
      toast.error('Failed to schedule pickup');
    } finally {
      setSubmitting(false);
    }
  };

  // Submit payment and create order
  const submitPayment = async () => {
    if (!paymentMode) {
      toast.error('Please select payment mode');
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API_URL}/api/cqc/checkout/payment`, {
        session_id: sessionId,
        payment_mode: paymentMode
      });
      toast.success(`Order ${res.data.order_id} created!`);
      setCurrentScreen('success');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create order');
    } finally {
      setSubmitting(false);
    }
  };

  // Render brand selection
  const renderBrandSelection = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-4">
        {brands.map(brand => (
          <div
            key={brand._id || brand.brand_id}
            onClick={() => { setSelectedBrand(brand); loadModels(brand._id || brand.brand_id); }}
            className={`p-4 bg-white rounded-xl border-2 cursor-pointer transition-all hover:shadow-lg text-center ${
              selectedBrand?._id === brand._id ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
            }`}
          >
            {brand.logo ? (
              <img src={brand.logo} alt={brand.name} className="w-12 h-12 mx-auto mb-2 object-contain" />
            ) : (
              <div className="w-12 h-12 mx-auto mb-2 bg-gray-100 rounded-lg flex items-center justify-center text-xl font-bold text-gray-600">
                {brand.name?.charAt(0)}
              </div>
            )}
            <p className="text-sm font-medium text-gray-800">{brand.name}</p>
          </div>
        ))}
      </div>
    </div>
  );

  // Render model selection
  const renderModelSelection = () => (
    <div className="space-y-4">
      {models.length === 0 ? (
        <p className="text-center text-gray-500 py-8">No models found for this brand</p>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
          {models.map(model => (
            <div
              key={model._id}
              onClick={() => { setSelectedModel(model); loadVariants(model._id); }}
              className={`p-4 bg-white rounded-xl border-2 cursor-pointer transition-all hover:shadow-lg ${
                selectedModel?._id === model._id ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
              }`}
            >
              {model.image ? (
                <img src={model.image} alt={model.name} className="w-full h-24 object-contain mb-2" />
              ) : (
                <div className="w-full h-24 bg-gray-100 rounded-lg flex items-center justify-center mb-2">
                  <Smartphone className="w-12 h-12 text-gray-400" />
                </div>
              )}
              <p className="text-sm font-medium text-gray-800 text-center">{model.name}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  // Render variant selection
  const renderVariantSelection = () => (
    <div className="space-y-4">
      {variants.length === 0 ? (
        <p className="text-center text-gray-500 py-8">No variants available</p>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
          {variants.map((variant, idx) => (
            <div
              key={variant._id || idx}
              onClick={() => setSelectedVariant(variant)}
              className={`p-4 bg-white rounded-xl border-2 cursor-pointer transition-all hover:shadow-lg text-center ${
                selectedVariant?._id === variant._id ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
              }`}
            >
              <p className="text-lg font-bold text-gray-800">{variant.ram || '?'}GB / {variant.storage || '?'}GB</p>
              <p className="text-sm text-gray-500">{variant.color || 'Any Color'}</p>
              {variant.cashify_price && (
                <p className="text-green-600 font-medium mt-2">Up to ₹{variant.cashify_price?.toLocaleString()}</p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );

  // Render MBP reveal
  const renderMbpReveal = () => (
    <div className="text-center py-8">
      <p className="text-gray-600 mb-2">Get up to</p>
      <p className="text-5xl font-bold mb-6" style={{ color: THEME.primary }}>₹{mbp?.toLocaleString() || '---'}</p>
      <p className="text-sm text-gray-500 mb-8">Answer a few questions to get your exact price</p>
    </div>
  );

  // Render yes/no questions
  const renderFunctionalBasics = () => (
    <div className="space-y-6">
      {screenConfig?.questions?.map(q => (
        <div key={q.id} className="bg-white rounded-xl p-4 border">
          <p className="font-medium text-gray-800 mb-3">{q.text}</p>
          <div className="flex gap-3">
            {q.options.map(opt => (
              <button
                key={opt.id}
                onClick={() => setCurrentAnswers(prev => ({ ...prev, [q.id]: opt.id }))}
                className={`flex-1 py-3 px-4 rounded-lg border-2 font-medium transition-all ${
                  currentAnswers[q.id] === opt.id 
                    ? 'border-blue-500 bg-blue-50 text-blue-700' 
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                {opt.text}
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );

  // Render multi-select tiles
  const renderMultiSelectTiles = () => (
    <div className="space-y-3">
      {screenConfig?.options?.map(opt => {
        const Icon = IconMap[opt.icon] || Box;
        const isSelected = (currentAnswers.selected || []).includes(opt.id);
        return (
          <div
            key={opt.id}
            onClick={() => {
              const current = currentAnswers.selected || [];
              if (isSelected) {
                setCurrentAnswers({ selected: current.filter(id => id !== opt.id) });
              } else {
                setCurrentAnswers({ selected: [...current, opt.id] });
              }
            }}
            className={`p-4 bg-white rounded-xl border-2 cursor-pointer transition-all flex items-center gap-4 ${
              isSelected ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <div className={`p-2 rounded-lg ${isSelected ? 'bg-blue-100' : 'bg-gray-100'}`}>
              <Icon className={`w-6 h-6 ${isSelected ? 'text-blue-600' : 'text-gray-500'}`} />
            </div>
            <span className="font-medium text-gray-800">{opt.text}</span>
            {isSelected && <Check className="ml-auto w-5 h-5 text-blue-600" />}
          </div>
        );
      })}
    </div>
  );

  // Render single select list
  const renderSingleSelectList = () => (
    <div className="space-y-3">
      {screenConfig?.options?.map(opt => (
        <div
          key={opt.id}
          onClick={() => setCurrentAnswers({ selected: opt.id })}
          className={`p-4 bg-white rounded-xl border-2 cursor-pointer transition-all ${
            currentAnswers.selected === opt.id ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
          }`}
        >
          <div className="flex items-center justify-between">
            <span className="font-medium text-gray-800">{opt.text}</span>
            {currentAnswers.selected === opt.id && <Check className="w-5 h-5 text-blue-600" />}
          </div>
          {opt.severity && opt.severity !== 'Good' && (
            <span className={`text-xs mt-1 inline-block px-2 py-0.5 rounded ${
              opt.severity === 'Salvage' ? 'bg-red-100 text-red-700' :
              opt.severity === 'High' ? 'bg-orange-100 text-orange-700' :
              'bg-yellow-100 text-yellow-700'
            }`}>{opt.severity}</span>
          )}
        </div>
      ))}
    </div>
  );

  // Render multi-section
  const renderMultiSection = () => (
    <div className="space-y-6">
      {screenConfig?.sections?.map(section => (
        <div key={section.id} className="bg-white rounded-xl p-4 border">
          <p className="font-semibold text-gray-800 mb-3">{section.title}</p>
          <div className="space-y-2">
            {section.options.map(opt => (
              <div
                key={opt.id}
                onClick={() => setCurrentAnswers(prev => ({ ...prev, [section.id]: opt.id }))}
                className={`p-3 rounded-lg border cursor-pointer transition-all ${
                  currentAnswers[section.id] === opt.id ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="text-gray-700">{opt.text}</span>
                  {currentAnswers[section.id] === opt.id && <Check className="w-4 h-4 text-blue-600" />}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );

  // Render checkbox grid (functional defects)
  const renderCheckboxGrid = () => (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      {screenConfig?.options?.map(opt => {
        const isSelected = (currentAnswers.selected || []).includes(opt.id);
        return (
          <div
            key={opt.id}
            onClick={() => {
              const current = currentAnswers.selected || [];
              if (isSelected) {
                setCurrentAnswers({ selected: current.filter(id => id !== opt.id) });
              } else {
                setCurrentAnswers({ selected: [...current, opt.id] });
              }
            }}
            className={`p-3 bg-white rounded-lg border-2 cursor-pointer transition-all ${
              isSelected ? 'border-red-500 bg-red-50' : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <div className="flex items-center gap-3">
              <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                isSelected ? 'border-red-500 bg-red-500' : 'border-gray-300'
              }`}>
                {isSelected && <Check className="w-3 h-3 text-white" />}
              </div>
              <span className="text-sm text-gray-700">{opt.text}</span>
            </div>
          </div>
        );
      })}
    </div>
  );

  // Render accessories
  const renderAccessories = () => (
    <div className="space-y-4">
      <p className="text-sm text-gray-600 text-center mb-4">Select accessories you have. Skip if none.</p>
      {screenConfig?.options?.map(opt => {
        const isSelected = (currentAnswers.selected || []).includes(opt.id);
        return (
          <div
            key={opt.id}
            onClick={() => {
              const current = currentAnswers.selected || [];
              if (isSelected) {
                setCurrentAnswers({ selected: current.filter(id => id !== opt.id) });
              } else {
                setCurrentAnswers({ selected: [...current, opt.id] });
              }
            }}
            className={`p-4 bg-white rounded-xl border-2 cursor-pointer transition-all ${
              isSelected ? 'border-green-500 bg-green-50' : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Package className={`w-5 h-5 ${isSelected ? 'text-green-600' : 'text-gray-400'}`} />
                <span className="font-medium text-gray-800">{opt.text}</span>
              </div>
              <span className="text-green-600 font-medium">+₹{opt.topup_amount}</span>
            </div>
          </div>
        );
      })}
    </div>
  );

  // Render OTP gate
  const renderOtpGate = () => (
    <div className="max-w-md mx-auto space-y-6">
      <div className="text-center">
        <Phone className="w-12 h-12 mx-auto text-blue-600 mb-4" />
        <p className="text-gray-600">Enter your mobile number to see your final price</p>
      </div>
      
      {!otpSent ? (
        <div className="space-y-4">
          <input
            type="tel"
            maxLength={10}
            value={mobile}
            onChange={(e) => setMobile(e.target.value.replace(/\D/g, ''))}
            placeholder="10-digit mobile number"
            className="w-full px-4 py-3 border-2 rounded-xl text-center text-xl tracking-wider"
          />
          <button
            onClick={sendOtp}
            disabled={submitting || mobile.length !== 10}
            className="w-full py-3 rounded-xl text-white font-medium disabled:opacity-50"
            style={{ backgroundColor: THEME.primary }}
          >
            {submitting ? 'Sending...' : 'Send OTP'}
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          <p className="text-sm text-center text-gray-500">OTP sent to {mobile}</p>
          {testOtp && <p className="text-xs text-center text-blue-600">Test OTP: {testOtp}</p>}
          <input
            type="text"
            maxLength={6}
            value={otp}
            onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
            placeholder="Enter 6-digit OTP"
            className="w-full px-4 py-3 border-2 rounded-xl text-center text-xl tracking-wider"
          />
          <button
            onClick={verifyOtp}
            disabled={submitting || otp.length !== 6}
            className="w-full py-3 rounded-xl text-white font-medium disabled:opacity-50"
            style={{ backgroundColor: THEME.success }}
          >
            {submitting ? 'Verifying...' : 'Verify & See Price'}
          </button>
        </div>
      )}
    </div>
  );

  // Render RBP reveal
  const renderRbpReveal = () => (
    <div className="text-center py-6">
      <CheckCircle className="w-16 h-16 mx-auto text-green-500 mb-4" />
      <p className="text-gray-600 mb-2">Your final price</p>
      <p className="text-6xl font-bold text-green-600 mb-4">₹{priceData?.rbp?.toLocaleString() || '---'}</p>
      
      {priceData && (
        <div className="bg-gray-50 rounded-xl p-4 text-left max-w-md mx-auto mt-6">
          <p className="font-semibold mb-2">Price Breakdown</p>
          <div className="space-y-1 text-sm">
            <div className="flex justify-between"><span>Maximum Price:</span><span>₹{priceData.mbp?.toLocaleString()}</span></div>
            <div className="flex justify-between text-red-600"><span>Deductions:</span><span>-₹{priceData.total_deduction_amount?.toLocaleString()}</span></div>
            {priceData.total_topup > 0 && (
              <div className="flex justify-between text-green-600"><span>Accessories:</span><span>+₹{priceData.total_topup?.toLocaleString()}</span></div>
            )}
            <div className="flex justify-between font-bold border-t pt-1"><span>Final Price:</span><span className="text-green-600">₹{priceData.rbp?.toLocaleString()}</span></div>
          </div>
        </div>
      )}
    </div>
  );

  // Render address form
  const renderAddressForm = () => (
    <div className="max-w-md mx-auto space-y-4">
      <input type="text" placeholder="Full Name" value={address.name} onChange={(e) => setAddress({...address, name: e.target.value})} className="w-full px-4 py-3 border rounded-lg" />
      <input type="tel" placeholder="Phone Number" value={address.phone} onChange={(e) => setAddress({...address, phone: e.target.value})} className="w-full px-4 py-3 border rounded-lg" />
      <input type="text" placeholder="Address Line 1" value={address.line1} onChange={(e) => setAddress({...address, line1: e.target.value})} className="w-full px-4 py-3 border rounded-lg" />
      <div className="grid grid-cols-2 gap-4">
        <input type="text" placeholder="City" value={address.city} onChange={(e) => setAddress({...address, city: e.target.value})} className="w-full px-4 py-3 border rounded-lg" />
        <input type="text" placeholder="Pincode" maxLength={6} value={address.pincode} onChange={(e) => setAddress({...address, pincode: e.target.value.replace(/\D/g, '')})} className="w-full px-4 py-3 border rounded-lg" />
      </div>
    </div>
  );

  // Render pickup slot
  const renderPickupSlot = () => (
    <div className="max-w-md mx-auto space-y-6">
      <div>
        <p className="font-medium mb-2">Select Date</p>
        <input type="date" value={pickupDate} onChange={(e) => setPickupDate(e.target.value)} min={new Date().toISOString().split('T')[0]} className="w-full px-4 py-3 border rounded-lg" />
      </div>
      <div>
        <p className="font-medium mb-2">Select Time Slot</p>
        <div className="grid grid-cols-2 gap-3">
          {['9AM - 12PM', '12PM - 3PM', '3PM - 6PM', '6PM - 9PM'].map(slot => (
            <button
              key={slot}
              onClick={() => setPickupSlot(slot)}
              className={`py-3 px-4 rounded-lg border-2 ${pickupSlot === slot ? 'border-blue-500 bg-blue-50' : 'border-gray-200'}`}
            >
              {slot}
            </button>
          ))}
        </div>
      </div>
    </div>
  );

  // Render payment mode
  const renderPaymentMode = () => (
    <div className="max-w-md mx-auto space-y-4">
      {[
        { id: 'upi', text: 'UPI', icon: Smartphone, desc: 'Instant payment to your UPI ID' },
        { id: 'bank', text: 'Bank Transfer', icon: Building, desc: 'NEFT/IMPS to your bank account' },
        { id: 'voucher', text: 'Store Voucher (+5% bonus)', icon: Gift, desc: 'Get 5% extra as store credit' }
      ].map(opt => (
        <div
          key={opt.id}
          onClick={() => setPaymentMode(opt.id)}
          className={`p-4 bg-white rounded-xl border-2 cursor-pointer transition-all ${
            paymentMode === opt.id ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
          }`}
        >
          <div className="flex items-center gap-4">
            <opt.icon className={`w-6 h-6 ${paymentMode === opt.id ? 'text-blue-600' : 'text-gray-500'}`} />
            <div>
              <p className="font-medium text-gray-800">{opt.text}</p>
              <p className="text-sm text-gray-500">{opt.desc}</p>
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  // Render success
  const renderSuccess = () => (
    <div className="text-center py-12">
      <CheckCircle className="w-20 h-20 mx-auto text-green-500 mb-6" />
      <h2 className="text-2xl font-bold text-gray-800 mb-2">Order Confirmed!</h2>
      <p className="text-gray-600">Our executive will contact you to confirm pickup.</p>
    </div>
  );

  // Get screen content
  const getScreenContent = () => {
    if (currentScreen === 'brand_selection') return renderBrandSelection();
    if (currentScreen === 'model_selection') return renderModelSelection();
    if (currentScreen === 'variant_selection') return renderVariantSelection();
    if (currentScreen === 'mbp_reveal') return renderMbpReveal();
    if (currentScreen === 'functional_basics') return renderFunctionalBasics();
    if (currentScreen === 'physical_defect_selector') return renderMultiSelectTiles();
    if (currentScreen === 'screen_physical') return renderSingleSelectList();
    if (currentScreen === 'screen_visual') return renderMultiSection();
    if (currentScreen === 'body_condition') return renderMultiSection();
    if (currentScreen === 'structural_integrity') return renderMultiSection();
    if (currentScreen === 'functional_defects') return renderCheckboxGrid();
    if (currentScreen === 'accessories') return renderAccessories();
    if (currentScreen === 'otp_gate') return renderOtpGate();
    if (currentScreen === 'rbp_reveal') return renderRbpReveal();
    if (currentScreen === 'address') return renderAddressForm();
    if (currentScreen === 'pickup_slot') return renderPickupSlot();
    if (currentScreen === 'payment_mode') return renderPaymentMode();
    if (currentScreen === 'success' || currentScreen === 'complete') return renderSuccess();
    return <p className="text-center text-gray-500">Loading...</p>;
  };

  // Get CTA action
  const getCtaAction = () => {
    if (currentScreen === 'otp_gate') return null; // Has its own buttons
    if (currentScreen === 'address') return submitAddress;
    if (currentScreen === 'pickup_slot') return submitSlot;
    if (currentScreen === 'payment_mode') return submitPayment;
    if (currentScreen === 'success' || currentScreen === 'complete') return null;
    return submitAnswer;
  };

  // Check if can proceed
  const canProceed = () => {
    if (currentScreen === 'brand_selection') return !!selectedBrand;
    if (currentScreen === 'model_selection') return !!selectedModel;
    if (currentScreen === 'variant_selection') return !!selectedVariant;
    if (currentScreen === 'mbp_reveal') return true;
    if (currentScreen === 'functional_basics') {
      const questions = screenConfig?.questions || [];
      return questions.every(q => currentAnswers[q.id]);
    }
    if (currentScreen === 'physical_defect_selector') return (currentAnswers.selected || []).length > 0;
    if (currentScreen === 'screen_physical') return !!currentAnswers.selected;
    if (currentScreen === 'screen_visual' || currentScreen === 'body_condition' || currentScreen === 'structural_integrity') {
      const sections = screenConfig?.sections || [];
      return sections.every(s => currentAnswers[s.id]);
    }
    if (currentScreen === 'functional_defects') return true; // Optional
    if (currentScreen === 'accessories') return true; // Optional
    if (currentScreen === 'rbp_reveal') return true;
    if (currentScreen === 'address') return address.name && address.pincode;
    if (currentScreen === 'pickup_slot') return pickupDate && pickupSlot;
    if (currentScreen === 'payment_mode') return !!paymentMode;
    return true;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: THEME.primaryLight }}>
              <Smartphone className="w-5 h-5" style={{ color: THEME.primary }} />
            </div>
            <div>
              <h1 className="font-bold" style={{ color: THEME.primary }}>Sell Your Phone</h1>
              <p className="text-xs text-gray-500">Get instant cash</p>
            </div>
          </div>
          {mbp > 0 && (
            <div className="text-right">
              <p className="text-xs text-gray-500">Up to</p>
              <p className="font-bold text-green-600">₹{mbp.toLocaleString()}</p>
            </div>
          )}
        </div>
      </div>

      {/* Progress */}
      <div className="max-w-2xl mx-auto px-4 py-3">
        <div className="flex items-center gap-1">
          {['Discovery', 'QC', 'Price', 'Checkout'].map((step, idx) => {
            const screenNum = screenConfig?.screen_number || 1;
            const layer = screenNum <= 4 ? 0 : screenNum <= 11 ? 1 : screenNum <= 14 ? 2 : 3;
            return (
              <div key={step} className="flex-1">
                <div className={`h-1 rounded-full ${idx <= layer ? 'bg-blue-500' : 'bg-gray-200'}`} />
                <p className={`text-xs mt-1 text-center ${idx <= layer ? 'text-blue-600' : 'text-gray-400'}`}>{step}</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-2xl mx-auto px-4 py-6">
        {/* Screen Title */}
        {screenConfig && (
          <div className="mb-6">
            <h2 className="text-xl font-bold text-gray-800">{screenConfig.title}</h2>
            <p className="text-gray-500">{screenConfig.subtitle}</p>
          </div>
        )}

        {/* Screen Content */}
        {getScreenContent()}
      </div>

      {/* Footer CTA */}
      {getCtaAction() && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t p-4">
          <div className="max-w-2xl mx-auto">
            <button
              onClick={getCtaAction()}
              disabled={submitting || !canProceed()}
              className="w-full py-4 rounded-xl text-white font-medium flex items-center justify-center gap-2 disabled:opacity-50"
              style={{ backgroundColor: THEME.primary }}
            >
              {submitting ? (
                <RefreshCw className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  {currentScreen === 'payment_mode' ? 'Place Order' : 'Continue'}
                  <ArrowRight className="w-5 h-5" />
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
